/**
 * GO PRO — inside the app, because the app is where the session lives.
 *
 * Anthony, 2026-09-10: "invalid token it says when going to a pay for
 * monthly 149."
 *
 * The first version was a static page that dug the JWT out of localStorage
 * by hand. That is the bug. supabase-js refreshes an access token only
 * while it is running, so a page opened in a fresh tab reads whatever was
 * last written -- up to an hour old, and an hour is exactly how long these
 * tokens last. A dead JWT is not a payment problem and it does not read
 * like one; it reads like the checkout is broken.
 *
 * Here the app's own client answers, and getSession() refreshes if it has
 * to. Nothing is scraped and nothing goes stale.
 *
 * The gateway itself was never the problem. Checked against Authorize.net
 * the same night: merchant Supercanic LLC, gateway 2847110, Chase
 * Paymentech, and the public client key in Railway matches the one the
 * gateway holds.
 */

import { db, api } from "../data/client.ts";
import { esc, mount, money } from "../ui/dom.ts";
import type { Role } from "../domain/permissions.ts";

type Offer = {
  shop: string; name: string; email: string; plan: string;
  already: boolean; subscription: string | null; may_buy: boolean;
  cents: number; label: string;
  /** RV Desk only: the rentals counted and how the figure was reached. */
  units?: number | null; price_line?: string | null;
  ready: boolean; why: string | null;
  clientKey: string | null; loginId: string | null; env: string;
  testing?: boolean;
  /** A card Authorize.net already holds for this shop. */
  on_file?: { last4: string; brand: string } | null;
  /** A repair shop chooses Pro or Solo. Null for an RV yard. */
  plans?: {
    pro: { cents: number; label: string };
    solo: { cents: number; label: string; cap: number; steps_to_cents: number; terms: string };
  } | null;
  tier?: "pro" | "solo" | null;
  error?: string;
};

/** ?test=1 goes through to the API, which decides whether it is allowed. */
const testFlag = (): string =>
  new URLSearchParams(location.hash.split("?")[1] ?? "").get("test") === "1"
  || new URLSearchParams(location.search).get("test") === "1" ? "?test=1" : "";

/** The app's own session, refreshed if it needs to be. */
async function bearer(): Promise<string> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return session?.access_token ?? "";
}

/**
 * Approved on Solo or Pro and not paid yet: the server's answer, see
 * GET /api/pro/owed. False on any failure -- a shop that cannot be asked
 * opens on its day as it always has, never on a checkout it may not owe.
 */
export async function owesFirstPayment(): Promise<boolean> {
  return (await owedTier()) !== null;
}

/** The plan this shop still owes its first payment on, or null. */
export async function owedTier(): Promise<"solo" | "pro" | null> {
  try {
    const r = await fetch(api("/api/pro/owed"), { headers: { Authorization: `Bearer ${await bearer()}` } });
    if (!r.ok) return null;
    const b = await r.json() as { owed?: boolean; tier?: string };
    return b.owed === true ? (b.tier === "solo" ? "solo" : "pro") : null;
  } catch { return null; }
}

declare global {
  interface Window { Accept?: { dispatchData: (d: unknown, cb: (r: AcceptRes) => void) => void } }
}
type AcceptRes = {
  messages: { resultCode: string; message?: Array<{ text?: string }> };
  opaqueData?: { dataDescriptor: string; dataValue: string };
};

/** Accept.js, loaded once, from the half of Authorize.net we are pointed at. */
function acceptJs(which: string): Promise<void> {
  if (window.Accept) return Promise.resolve();
  return new Promise((ok, no) => {
    const s = document.createElement("script");
    s.src = which === "production"
      ? "https://js.authorize.net/v1/Accept.js"
      : "https://jstest.authorize.net/v1/Accept.js";
    s.onload = () => ok();
    s.onerror = () => no(new Error("The card service did not load."));
    document.head.appendChild(s);
  });
}

/**
 * CHANGE THE CARD on a subscription the shop already has. Nothing is
 * charged: the server checks the card with its CVV at $0, saves it, and
 * moves the same subscription onto it (POST /api/pro/card).
 */
async function cardForm(d: Offer): Promise<void> {
  const plan = d.tier === "solo" ? "Solo" : "Pro";
  mount(`
    <div class="page">
      <header class="page-head"><a class="back" href="#/billing">&larr; Plan and billing</a><h1>Change the card</h1>
        <p>${esc(d.shop)} is on ${plan}. Put in the card you want the monthly bill to go on.</p></header>
      <div class="card pad">
        ${d.on_file ? `<p class="fine">The bill goes on your ${esc(d.on_file.brand)} ending ${esc(d.on_file.last4)} now.</p>` : ""}
        <label class="techfield"><span>Card number</span>
          <input id="pcn" inputmode="numeric" autocomplete="cc-number"></label>
        <div class="prorow">
          <label class="techfield"><span>Month</span>
            <input id="pmm" inputmode="numeric" autocomplete="cc-exp-month" maxlength="2"></label>
          <label class="techfield"><span>Year</span>
            <input id="pyy" inputmode="numeric" autocomplete="cc-exp-year" maxlength="4"></label>
          <label class="techfield"><span>CVV</span>
            <input id="pcvv" inputmode="numeric" autocomplete="cc-csc" maxlength="4"></label>
        </div>
        <label class="techfield"><span>Billing zip</span>
          <input id="pzip" inputmode="numeric" autocomplete="postal-code"></label>
        <button class="btn primary wide" id="pgo">Save this card</button>
        <div class="msg-slot" id="pmsg"></div>
        <p class="fine">Nothing is charged today. Your bank checks the card and its CVV, then your
          next monthly bill goes on it. Same plan, same rate. Your card is handled by
          Authorize.net and never reaches our servers.</p>
      </div>
    </div>`);

  const say = (html: string) => {
    const m = document.getElementById("pmsg");
    if (m) m.innerHTML = html;
  };
  const reset = () => {
    const b = document.getElementById("pgo") as HTMLButtonElement | null;
    if (b) { b.disabled = false; b.textContent = "Save this card"; }
  };
  try { await acceptJs(d.env); } catch {
    say(`<p class="msg bad">The card service did not load. Try again in a minute.</p>`);
    return;
  }

  document.getElementById("pgo")?.addEventListener("click", () => {
    const v = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? "";
    const cn = v("pcn").replace(/\s+/g, "");
    if (!cn || !v("pmm") || !v("pyy")) { say(`<p class="msg bad">Fill the card in first.</p>`); return; }
    if (!/^[0-9]{3,4}$/.test(v("pcvv"))) {
      say(`<p class="msg bad">Enter the CVV, the 3 or 4 digits on the card.</p>`);
      return;
    }
    const b = document.getElementById("pgo") as HTMLButtonElement;
    b.disabled = true; b.textContent = "Saving…";
    say("");
    window.Accept!.dispatchData({
      authData: { clientKey: d.clientKey, apiLoginID: d.loginId },
      cardData: { cardNumber: cn, month: v("pmm"), year: v("pyy"), cardCode: v("pcvv"), zip: v("pzip") },
    }, (res) => {
      if (res.messages.resultCode !== "Ok" || !res.opaqueData) {
        const why = (res.messages.message ?? []).map((m) => m.text).filter(Boolean).join(" ");
        say(`<p class="msg bad">${esc(why || "The card was not accepted.")}</p>`);
        reset();
        return;
      }
      void (async () => {
        try {
          const r = await fetch(api("/api/pro/card"), {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${await bearer()}` },
            /* last4 only finds a card the profile already holds; the card
               itself travels as the one-use descriptor. */
            body: JSON.stringify({ opaque_data: res.opaqueData, zip: v("pzip"), last4: cn.slice(-4) }),
          });
          const out = await r.json() as { error?: string; last4?: string; subscription?: string };
          if (out.error) { say(`<p class="msg bad">${esc(out.error)}</p>`); reset(); return; }
          mount(`<div class="page"><header class="page-head"><a class="back" href="#/billing">&larr; Plan and billing</a><h1>Change the card</h1></header>
            <div class="card pad prostate ok">
              <b>Card saved</b>
              <p>Your ${plan} bill goes on the card ending ${esc(out.last4 ?? "")} from now on. Nothing was charged today.</p>
              <a class="btn primary" href="/app">Open the desk</a>
              <p class="fine">Subscription ${esc(out.subscription ?? "")}.</p>
            </div></div>`);
        } catch {
          say(`<p class="msg bad">We could not tell whether that went through. Ring us on (951) 904-2477 and we will check.</p>`);
          reset();
        }
      })();
    });
  });
}

/**
 * SOLO, THE THIRD PLAN. Anthony, 2026-10-05: Pro with everything for $50 a
 * month until the shop has 100 closed and paid jobs, then $149.
 *
 * The choice is two buttons above the card, opening on what the shop
 * asked for at signup. Picking Solo shows its terms in full and a box the
 * owner ticks before the button will go, because the price rising later is
 * the one thing about Solo a shop must not find out afterwards. The server
 * refuses Solo without the tick as well; the box here is only the part a
 * person reads.
 */
export async function proPage(_role: Role = "viewer"): Promise<void> {
  mount(`<div class="page"><div class="loading">Reading&hellip;</div></div>`);

  let d: Offer;
  try {
    const r = await fetch(api(`/api/pro${testFlag()}`), { headers: { Authorization: `Bearer ${await bearer()}` } });
    d = await r.json() as Offer;
  } catch {
    mount(`<div class="page"><div class="card pad">
      <div class="empty bad">That did not load. Try again in a minute.</div></div></div>`);
    return;
  }

  const box = (title: string, body: string, good = false) => `
    <div class="page"><header class="page-head"><a class="back" href="#/billing">&larr; Plan and billing</a><h1>Go Pro</h1></header>
      <div class="card pad prostate${good ? " ok" : ""}">
        <b>${esc(title)}</b><p>${body}</p>
        <a class="btn" href="/app">Back to the desk</a>
      </div></div>`;

  if (d.error) return mount(box("We could not check your account", esc(d.error)));
  /* A SHOP ALREADY PAYING BY CARD can change the card here. Anthony,
     2026-10-22, for Brian Huey: the only answer this page had was "ring
     us". Only a gateway subscription (digits) has a card to change. */
  if (d.already && d.may_buy && d.ready && /^[0-9]+$/.test(d.subscription ?? "")) {
    return cardForm(d);
  }
  if (d.already) {
    return mount(box(`${d.shop} is already on ${d.tier === "solo" ? "Solo" : "Pro"}`,
      `Nothing to pay. If something is not working, ring us on (951) 904-2477 rather than
       starting a second subscription.${d.subscription
        ? ` <span class="quiet">Subscription ${esc(d.subscription)}.</span>` : ""}`, true));
  }
  if (!d.may_buy) {
    return mount(box("Ask the owner",
      `Only the person who runs ${esc(d.shop)} can put the shop on a monthly bill.`));
  }
  if (!d.ready) {
    return mount(box("Not just now",
      `${esc(d.why ?? "")} Ring us on (951) 904-2477 and we will set it up with you.`));
  }

  mount(`
    <div class="page">
      <header class="page-head"><a class="back" href="#/billing">&larr; Plan and billing</a><h1>Go Pro</h1>
        <p>${d.testing
          ? "<b>Test run.</b> A real card, a real subscription, one dollar. Cancel it after."
          : "Everything the shop runs on, on one bill."}</p></header>

      <div class="card pad">
        ${d.plans ? `
        <div class="plans-pick" role="radiogroup" aria-label="Which plan">
          <button type="button" class="plan-pick" data-tier="pro" role="radio">
            <b>Pro</b><span>${esc(money(d.plans.pro.cents / 100))} a month</span>
            <small>Everything, for any size of shop.</small></button>
          <button type="button" class="plan-pick" data-tier="solo" role="radio">
            <b>Solo</b><span>${esc(money(d.plans.solo.cents / 100))} a month</span>
            <small>Everything in Pro, until your first ${d.plans.solo.cap} closed and paid jobs.</small></button>
        </div>` : ""}
        <div class="proprice">
          <span class="profig">${esc(money(d.cents / 100))}</span>
          <span class="properm">/ month</span>
          <span class="proshop">for<b>${esc(d.shop)}</b></span>
        </div>
        ${d.price_line ? `<p class="fine">${esc(d.price_line)}</p>` : ""}
        ${d.units != null
          /* RV DESK. Anthony, 2026-09-26: "they get website, marketing, and
             a full shop desk custom to their business and fleet." The
             auto list names repair manuals, which a rental yard has no use
             for. units is only ever sent for an RV shop. */
          ? `<ul class="prolist">
          <li>Your own website, built and kept current</li>
          <li>Marketing, and a listing on rvtravelagency.com</li>
          <li>A desk built around your business and your fleet</li>
          <li>Invoicing and card payments</li>
        </ul>`
          : `<ul class="prolist">
          <li id="pcap">5,000 customers and unlimited AI</li>
          <li>Invoicing and card payments</li>
          <li>Payroll &mdash; flat rate, hourly, commission</li>
          <li>ProDemand and ALLDATA in the panel</li>
          <li>Eligibility for dispatched leads</li>
        </ul>`}

        <!-- NO CHARGE WITHOUT THE CVV. Anthony, 2026-09-28. A saved card has
             no CVV, so the old "Use this card" button charged Matt Bolen's
             Visa with none. The card on file is named, and typed in again
             with its CVV; the saved copy is reused, not stored twice. -->
        ${d.on_file ? `
          <p class="fine">We have your ${esc(d.on_file.brand)} ending ${esc(d.on_file.last4)} saved.
            A card is only charged with its CVV, so enter it below with the 3 or 4 digits.</p>` : ""}

        <label class="techfield"><span>Card number</span>
          <input id="pcn" inputmode="numeric" autocomplete="cc-number"></label>
        <div class="prorow">
          <label class="techfield"><span>Month</span>
            <input id="pmm" inputmode="numeric" autocomplete="cc-exp-month" maxlength="2"></label>
          <label class="techfield"><span>Year</span>
            <input id="pyy" inputmode="numeric" autocomplete="cc-exp-year" maxlength="4"></label>
          <label class="techfield"><span>CVV</span>
            <input id="pcvv" inputmode="numeric" autocomplete="cc-csc" maxlength="4"></label>
        </div>
        <label class="techfield"><span>Billing zip</span>
          <input id="pzip" inputmode="numeric" autocomplete="postal-code"></label>

        ${d.plans ? `
        <div id="psolo" hidden>
          <p class="fine"><b>${esc(d.plans.solo.terms)}</b></p>
          <label class="proagree"><input type="checkbox" id="pagree">
            <span>I agree that Solo moves to ${esc(money(d.plans.solo.steps_to_cents / 100))} a month
              after ${d.plans.solo.cap} closed and paid jobs, unless I cancel first.</span></label>
        </div>` : ""}

        <button class="btn primary wide" id="pgo">Start Pro &mdash; ${esc(money(d.cents / 100))} a month</button>
        <div class="msg-slot" id="pmsg"></div>
        <p class="fine">The first month is charged today, then monthly on the same day until
          you tell us to stop. We only charge a card whose CVV your bank confirms.
          <span id="pkeep">The rate you start on is the rate you keep.</span> Your card is handled
          by Authorize.net and never reaches our servers.</p>
      </div>
    </div>`);

  const say = (html: string) => {
    const m = document.getElementById("pmsg");
    if (m) m.innerHTML = html;
  };

  /* Which plan is chosen. Everything that shows a figure reads `cents`,
     so changing the plan changes every figure on the page at once. */
  let tier: "pro" | "solo" = d.plans && d.tier === "solo" ? "solo" : "pro";
  let cents = d.plans ? d.plans[tier].cents : d.cents;
  const word = () => tier === "solo" ? "Solo" : "Pro";
  const reset = () => {
    const b = document.getElementById("pgo") as HTMLButtonElement | null;
    if (b) { b.disabled = false; b.textContent = `Start ${word()} — ${money(cents / 100)} a month`; }
  };
  const choose = (t: "pro" | "solo") => {
    if (!d.plans) return;
    tier = t; cents = d.plans[t].cents;
    document.querySelectorAll<HTMLElement>(".plan-pick").forEach((b) => {
      const on = b.dataset["tier"] === t;
      b.classList.toggle("on", on);
      b.setAttribute("aria-checked", String(on));
    });
    const fig = document.querySelector(".profig");
    if (fig) fig.textContent = money(cents / 100);
    const cap = document.getElementById("pcap");
    if (cap) cap.textContent = t === "solo"
      ? `${d.plans.solo.cap} closed and paid jobs, unlimited AI`
      : "5,000 customers and unlimited AI";
    const solo = document.getElementById("psolo");
    if (solo) solo.hidden = t !== "solo";
    /* Pro keeps its rate for life. Solo does not, by design, so the
       sentence that says it would be false on Solo is swapped, not left. */
    const keep = document.getElementById("pkeep");
    if (keep) keep.textContent = t === "solo"
      ? `Solo stays at ${money(d.plans.solo.cents / 100)} until ${d.plans.solo.cap} closed and paid jobs, then moves to ${money(d.plans.solo.steps_to_cents / 100)} with an email first.`
      : "The rate you start on is the rate you keep.";
    say("");
    reset();
  };
  document.querySelectorAll<HTMLElement>(".plan-pick").forEach((b) =>
    b.addEventListener("click", () => choose(b.dataset["tier"] === "solo" ? "solo" : "pro")));
  choose(tier);

  try { await acceptJs(d.env); } catch {
    say(`<p class="msg bad">The card service did not load. Try again in a minute.</p>`);
    return;
  }

  /** What to do once the gateway has answered. */
  const finish = (out: { error?: string; already?: boolean; subscription?: string; since?: string; next_bill?: string; note?: string; testing?: boolean }) => {
    if (out.error) { say(`<p class="msg bad">${esc(out.error)}</p>`); reset(); return; }
    mount(`<div class="page"><header class="page-head"><a class="back" href="#/billing">&larr; Plan and billing</a><h1>Go Pro</h1></header>
      <div class="card pad prostate ok">
        <b>${out.testing ? "The test went through" : out.already ? `${esc(d.shop)} was already on a paid plan` : `You are on ${word()}`}</b>
        <p>${out.already ? "Nothing was charged twice."
          : `${esc(money(cents / 100))} paid today for your first month${out.next_bill
             ? `, then monthly from ${esc(out.next_bill)}` : ""}.
             ${out.testing ? "Cancel it in Authorize.net." : "Everything is switched on now."}`}</p>
        ${out.note ? `<p><b>${esc(out.note)}</b></p>` : ""}
        <a class="btn primary" href="/app">Open the desk</a>
        <p class="fine">Subscription ${esc(out.subscription ?? "")}. Keep this number if you
          ever ring us about the bill.</p>
      </div></div>`);
  };

  document.getElementById("pgo")?.addEventListener("click", () => {
    const v = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? "";
    const cn = v("pcn").replace(/\s+/g, "");
    if (!cn || !v("pmm") || !v("pyy")) {
      say(`<p class="msg bad">Fill the card in first.</p>`);
      return;
    }
    if (!/^[0-9]{3,4}$/.test(v("pcvv"))) {
      say(`<p class="msg bad">Enter the CVV, the 3 or 4 digits on the card. We only charge a card with its CVV.</p>`);
      return;
    }
    const agreed = (document.getElementById("pagree") as HTMLInputElement | null)?.checked === true;
    if (tier === "solo" && !agreed) {
      say(`<p class="msg bad">Tick the box to agree that Solo moves to ${esc(money((d.plans?.solo.steps_to_cents ?? 0) / 100))} a month after ${d.plans?.solo.cap ?? 100} closed and paid jobs.</p>`);
      return;
    }
    const b = document.getElementById("pgo") as HTMLButtonElement;
    b.disabled = true; b.textContent = "Starting…";
    say("");

    window.Accept!.dispatchData({
      authData: { clientKey: d.clientKey, apiLoginID: d.loginId },
      cardData: { cardNumber: cn, month: v("pmm"), year: v("pyy"), cardCode: v("pcvv"), zip: v("pzip") },
    }, (res) => {
      if (res.messages.resultCode !== "Ok" || !res.opaqueData) {
        const why = (res.messages.message ?? []).map((m) => m.text).filter(Boolean).join(" ");
        say(`<p class="msg bad">${esc(why || "The card was not accepted.")}</p>`);
        reset();
        return;
      }
      /* The card is gone from this page now. What travels is a one-use
         descriptor Authorize.net minted in the browser. */
      void (async () => {
        try {
          const r = await fetch(api(`/api/pro${testFlag()}`), {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${await bearer()}` },
            /* The zip goes to the SERVER too, not only to Accept.js.
               Authorize.net runs an address check when it stores a card,
               and a billTo with only a name fails it -- E00027, "missing
               or invalid required fields", which is what Matt Bolen hit
               five times. */
            body: JSON.stringify({ opaque_data: res.opaqueData, zip: v("pzip"), tier, agree_solo: tier === "solo" && agreed }),
          });
          finish(await r.json() as Record<string, never>);
        } catch {
          say(`<p class="msg bad">We could not tell whether that went through. Please do NOT
            try again &mdash; ring us on (951) 904-2477 and we will check.</p>`);
        }
      })();
    });
  });
}
